import React from "react";
import styled from "styled-components";

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }
`;

const SectionTitle = styled.h2`
  color: #183967;
  font-size: 18px;
  font-weight: 700;
  margin: 20px 0 12px;

  &:first-child {
    margin-top: 0;
  }
`;

const Paragraph = styled.p`
  color: #111827;
  font-size: 15px;
  line-height: 1.75;
  margin-bottom: 12px;
  text-align: justify;
`;

const TableCard = styled.div`
  background: #ffffff;
  border-radius: 18px;
  padding: 18px;
  margin: 16px 0 22px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
  overflow-x: auto;
  width: 100%;
  max-width: 100%;

  @media (max-width: 768px) {
    padding: 12px;
    margin: 12px 0 15px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: 680px;
  border-collapse: separate;
  border-spacing: 0;
  color: #111827;
  font-size: 13px;

  th {
    text-align: left;
    background: #dce9fb;
    color: #1f3556;
    font-weight: 700;
    padding: 13px 16px;
    border-bottom: none;
    line-height: 1.45;
    vertical-align: top;
  }

  th:first-child {
    border-radius: 8px 0 0 8px;
  }

  th:last-child {
    border-radius: 0 8px 8px 0;
  }

  td {
    padding: 13px 16px;
    color: #111827;
    border-bottom: 1px solid #d8dee6;
    line-height: 1.55;
    vertical-align: top;
    overflow-wrap: break-word;
  }

  tr:nth-child(even) td {
    background: #f7f9fb;
  }

  tr:last-child td {
    border-bottom: none;
  }

  th:first-child,
  td:first-child {
    width: 64px;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    min-width: 620px;
    font-size: 12px;

    th,
    td {
      padding: 8px;
    }
  }
`;

const ChartCard = styled.div`
  background: #ffffff;
  border-radius: 18px;
  padding: 24px 28px 20px;
  margin: 16px 0 22px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
  width: 100%;
  max-width: 100%;

  @media (max-width: 768px) {
    padding: 16px 14px 14px;
  }
`;

const ChartLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 30px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const ChartFigure = styled.div`
  min-width: 0;
`;

const ChartGraph = styled.div`
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 10px;
  align-items: stretch;
`;

const YAxis = styled.div`
  position: relative;
  height: 210px;
  color: #183a73;
  font-size: 14px;
  font-weight: 700;

  @media (max-width: 768px) {
    height: 180px;
    font-size: 12px;
  }
`;

const YAxisTick = styled.span`
  position: absolute;
  right: 0;
  bottom: ${(props) => props.$bottom}%;
  transform: translateY(50%);
`;

const ChartPlot = styled.div`
  position: relative;
  height: 210px;
  border-left: 2px solid #d9e2e6;
  border-bottom: 2px solid #d9e2e6;
  padding: 0 22px;
  overflow: visible;

  @media (max-width: 768px) {
    height: 180px;
    padding: 0 10px;
  }
`;

const GridLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${(props) => props.$bottom}%;
  border-top: 1px dashed #cfd5dd;
  pointer-events: none;
`;

const BarsRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-evenly;
  gap: 18px;
  height: 100%;
  width: 100%;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const BarSlot = styled.div`
  position: relative;
  height: 100%;
  flex: 1 1 0;
  min-width: 0;
  z-index: 1;
`;

const Bar = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  width: clamp(36px, 28%, 44px);
  min-height: 6px;
  background: #ff971f;
  transform: translateX(-50%);
  height: ${(props) => props.$height}%;
`;

const BarValue = styled.div`
  position: absolute;
  left: 50%;
  bottom: calc(${(props) => props.$height}% + 5px);
  transform: translateX(-50%);
  font-size: 13px;
  font-weight: 700;
  color: #183a73;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const ChartLabelsRow = styled.div`
  display: flex;
  justify-content: space-evenly;
  gap: 18px;
  margin-left: 52px;
  padding: 8px 22px 0;

  @media (max-width: 768px) {
    gap: 8px;
    margin-left: 52px;
    padding: 8px 10px 0;
  }
`;

const BarLabel = styled.div`
  flex: 1 1 0;
  min-width: 0;
  font-size: 12px;
  color: #061a2e;
  text-align: center;
  line-height: 1.35;
  overflow-wrap: break-word;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  font-size: 12px;
  color: #061a2e;
  white-space: nowrap;

  @media (max-width: 768px) {
    justify-content: center;
    white-space: normal;
  }
`;

const LegendDot = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ff971f;
  display: inline-block;
  flex: 0 0 auto;
`;

const chartMaxValue = 2500;
const yAxisTicks = [2500, 2000, 1000, 500, 0];
const chartData = [
  { label: "Government of India", amount: 2190, pct: 73 },
  { label: "State Government / UTs", amount: 780, pct: 26 },
  {
    label: "Organisations & Individuals interested in the Welfare of Minorities",
    amount: 30,
    pct: 1,
  },
];

const getChartPosition = (value) => (value / chartMaxValue) * 100;

const CapitalStructureContent = () => {
  return (
    <Container>
      <SectionTitle>Authorised Share Capital</SectionTitle>
      <Paragraph>
        NMDFC was incorporated on the 30th September, 1994 under Section 25 of the Companies Act 1956
        (Now Section 8 of Companies Act 2013) with an initial Authorized Share Capital of Rs. 500.00
        Crores. The Authorized Share Capital of NMDFC has been enhanced from Rs. 500.00 Crores to Rs.
        3000.00 Crores over a period of 20 years as detailed below:-
      </Paragraph>

      <TableCard>
        <StyledTable>
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Enhancement of Authorized Share Capital (Rs. in Crores)</th>
              <th>Approval from the Govt. of India</th>
              <th>Approval from the Regional Director, Company Affairs</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>01</td>
              <td>500.00 to 650.00</td>
              <td>22.12.2004</td>
              <td>28.07.2005</td>
            </tr>
            <tr>
              <td>02</td>
              <td>650.00 to 750.00</td>
              <td>19.11.2007</td>
              <td>26.02.2008</td>
            </tr>
            <tr>
              <td>03</td>
              <td>750.00 to 850.00</td>
              <td>22.09.2008</td>
              <td>29.10.2008</td>
            </tr>
            <tr>
              <td>04</td>
              <td>850.00 to 1000.00</td>
              <td>09.09.2009</td>
              <td>30.09.2009</td>
            </tr>
            <tr>
              <td>05</td>
              <td>1000.00 to 1500.00</td>
              <td>16.07.2010</td>
              <td>12.08.2010</td>
            </tr>
            <tr>
              <td>06</td>
              <td>1500.00 to 3000.00</td>
              <td>17.02.2015</td>
              <td>24.03.2015</td>
            </tr>
          </tbody>
        </StyledTable>
      </TableCard>

      <ChartCard>
        <ChartLayout>
          <ChartFigure>
            <ChartGraph>
              <YAxis aria-hidden="true">
                {yAxisTicks.map((tick) => (
                  <YAxisTick key={tick} $bottom={getChartPosition(tick)}>
                    {tick}
                  </YAxisTick>
                ))}
              </YAxis>
              <ChartPlot>
                {yAxisTicks
                  .filter((tick) => tick !== 0)
                  .map((tick) => (
                    <GridLine key={tick} $bottom={getChartPosition(tick)} />
                  ))}
                <BarsRow>
                  {chartData.map((item) => {
                    const height = getChartPosition(item.amount);

                    return (
                      <BarSlot key={item.label}>
                        <BarValue $height={height}>{item.pct}%</BarValue>
                        <Bar $height={height} />
                      </BarSlot>
                    );
                  })}
                </BarsRow>
              </ChartPlot>
            </ChartGraph>
            <ChartLabelsRow>
              {chartData.map((item) => (
                <BarLabel key={item.label}>{item.label}</BarLabel>
              ))}
            </ChartLabelsRow>
          </ChartFigure>
          <Legend>
            <LegendDot />
            Amount (Rs. in Crores)
          </Legend>
        </ChartLayout>
      </ChartCard>

      <Paragraph>
        The Authorized Share Capital of Rs. 3000.00 Crores is to be contributed by various agencies in
        the following manner:
      </Paragraph>

      <TableCard>
        <StyledTable>
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Source</th>
              <th>Amount (Rs. in Crore)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>01</td>
              <td>Govt. of India</td>
              <td>Rs. 2190.00 Crores (73%)</td>
            </tr>
            <tr>
              <td>02</td>
              <td>State Government / UTs</td>
              <td>Rs. 780.00 Crores (26%)</td>
            </tr>
            <tr>
              <td>03</td>
              <td>Organizations & Individuals interested in the Welfare of Minorities</td>
              <td>Rs. 30.00 Crores (1%)</td>
            </tr>
          </tbody>
        </StyledTable>
      </TableCard>

      <SectionTitle>Actual Contribution</SectionTitle>
      <Paragraph>
        The Authorized Share capital of NMDFC is Rs.3000.00 crores with 73% (2190.00 crores)
        contribution from Central Government, 26% (780.00 crores) contribution from various State/UT
        Governments and 1% (30.00 crores) from individuals/organizations having interest in minorities.
      </Paragraph>
      <Paragraph>
        The paid up share capital of NMDFC as on 31.03.2026 is Rs.2630.49 crores with contribution of
        Rs.2190.00 crores by Government of India and Rs. 440.49 crores by State/UT Governments i.e.
        100% and 56.47% respectively.
      </Paragraph>

      <TableCard>
        <StyledTable>
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Particulars</th>
              <th>% age Earmarked</th>
              <th>Equity to be Contributed (Rs. in Crores)</th>
              <th>Actual Equity Contribution (Rs. in Crores)</th>
              <th>% age Contributed</th>
              <th>Balance (to be contributed) (Rs. in Crores)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>01</td>
              <td>Govt. of India</td>
              <td>73</td>
              <td>2190.00</td>
              <td>2190.00</td>
              <td>100</td>
              <td>0.00</td>
            </tr>
            <tr>
              <td>02</td>
              <td>State / UTs</td>
              <td>26</td>
              <td>780.00</td>
              <td>440.49</td>
              <td>56.47</td>
              <td>339.51</td>
            </tr>
            <tr>
              <td>03</td>
              <td>Organisations and individuals interested in welfare of minorities</td>
              <td>1</td>
              <td>30.00</td>
              <td>0.01</td>
              <td>0.03</td>
              <td>29.99</td>
            </tr>
            <tr>
              <td>-</td>
              <td>
                <strong>Total</strong>
              </td>
              <td>
                <strong>100</strong>
              </td>
              <td>
                <strong>3000.00</strong>
              </td>
              <td>
                <strong>2630.49</strong>
              </td>
              <td>
                <strong>87.68</strong>
              </td>
              <td>
                <strong>369.50</strong>
              </td>
            </tr>
          </tbody>
        </StyledTable>
      </TableCard>
    </Container>
  );
};

export default CapitalStructureContent;
